import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PostComposer from "@/components/social/PostComposer";
import CommunityDocsPanel from "@/components/CommunityDocsPanel";
import PostCard from "@/components/social/PostCard";
import { Hash, TrendingUp, Sparkles, Loader2, ShieldAlert, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function SocialFeed() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag");
  const singlePostId = params.get("post");

  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [trending, setTrending] = useState([]);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/feed");
  }, [user, loading, nav]);

  const loadFeed = useCallback(async (reset = false) => {
    if (!user) return;
    if (fetching) return;
    setFetching(true);
    try {
      const params = { limit: 20 };
      if (!reset && cursor) params.cursor = cursor;
      if (tag) params.hashtag = tag;
      if (singlePostId && reset) {
        try {
          const single = await api.get(`/social/posts/${singlePostId}`);
          setPosts([single.data]);
          setCursor(null);
          setHasMore(false);
          setInitialLoaded(true);
          return;
        } catch { /* fall through */ }
      }
      const r = await api.get("/social/feed", { params });
      const newPosts = r.data.posts || [];
      setPosts((prev) => (reset ? newPosts : [...prev, ...newPosts]));
      setCursor(r.data.next_cursor);
      setHasMore(!!r.data.next_cursor);
      setInitialLoaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed failed to load");
    } finally {
      setFetching(false);
    }
  }, [user, tag, singlePostId, cursor, fetching]);

  // Initial + tag-change reset
  useEffect(() => {
    setPosts([]); setCursor(null); setHasMore(true); setInitialLoaded(false);
    loadFeed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag, singlePostId, user?.id]);

  // Trending hashtags
  useEffect(() => {
    if (!user) return;
    api.get("/social/hashtags/trending", { params: { limit: 8 } })
      .then((r) => setTrending(r.data.tags || []))
      .catch(() => {});
  }, [user, posts.length]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !fetching) loadFeed(false);
    }, { rootMargin: "300px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, fetching, loadFeed]);

  const onCreated = (post) => setPosts((prev) => [post, ...prev]);
  const onChange = (updated) => setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  const onDeleted = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));

  const clearFilter = () => setParams({});

  if (loading) return <div className="max-w-2xl mx-auto px-4 py-12 text-white/50">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      {/* Header */}
      <div className="mb-5 sm:mb-8 flex items-baseline justify-between gap-3">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] text-sky/80 uppercase mb-1">// PJP · SOCIAL FEED</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {tag ? (
              <span className="text-white">#{tag}</span>
            ) : singlePostId ? (
              <span className="text-white">Post</span>
            ) : (
              <span
                className="bg-gradient-to-r from-sky via-[#7EC8FF] to-[#A8DBFF] bg-clip-text text-transparent"
                style={{ textShadow: "0 0 40px rgba(75,156,255,0.35)" }}
              >
                Hot Mic
              </span>
            )}
          </h1>
          <div className="text-white/50 text-sm mt-1">
            {tag ? "Filtered by hashtag" : singlePostId ? "Direct link" : "Where 26,500 aviation pros hang out between legs."}
          </div>
        </div>
        {(tag || singlePostId) && (
          <button
            onClick={clearFilter}
            data-testid="clear-filter"
            className="mono text-xs px-3 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/70 hover:text-white hover:border-white/25 min-h-[40px]"
          >
            Clear
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] gap-6">
        {/* Main column */}
        <div className="space-y-5 min-w-0">
          {/* Identity gate nudge — proactive banner for members who can't post yet */}
          {user && !user.identity_verified && !user.is_admin && !user.founding_member && !tag && !singlePostId && (
            <Link
              to="/verify/identity"
              data-testid="hotmic-identity-nudge"
              className="block relative rounded-xl overflow-hidden border border-orange-hud/40 bg-orange-hud/[0.06] hover:border-orange-hud/70 hover:bg-orange-hud/[0.10] transition-colors p-4"
            >
              {/* Ambient glow */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(255,138,61,0.18), transparent 55%)" }} />
              <div className="relative flex items-center gap-3">
                <div className="p-2 rounded-md border border-orange-hud/50 bg-orange-hud/[0.10] flex-shrink-0">
                  <ShieldAlert className="h-4 w-4 text-orange-hud" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mono text-[10px] tracking-[0.24em] uppercase text-orange-hud">// IDENTITY · REQUIRED</div>
                  <div className="text-white font-display text-[15px] font-semibold mt-0.5 leading-tight">Verify to post, comment, or DM.</div>
                  <div className="text-white/60 text-[12px] mt-0.5">30-second upload of your passport or driver's license. Auto-verified instantly.</div>
                </div>
                <div className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-orange-hud/50 text-orange-hud flex items-center gap-1.5 flex-shrink-0">
                  Verify <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          )}
          {!tag && !singlePostId && <PostComposer onCreated={onCreated} />}
          {!tag && !singlePostId && <CommunityDocsPanel />}

          {!initialLoaded && (
            <div className="card !p-8 text-center text-white/50">
              <Loader2 className="h-6 w-6 mx-auto animate-spin text-sky" />
              <div className="mt-2 text-sm">Fetching the feed…</div>
            </div>
          )}

          {initialLoaded && posts.length === 0 && (
            <div className="card !p-8 text-center" data-testid="empty-feed">
              <Sparkles className="h-8 w-8 mx-auto text-sky mb-3" />
              <div className="font-display text-xl text-white mb-1">
                {tag ? `Nothing tagged #${tag} yet` : "No posts yet"}
              </div>
              <div className="text-white/50 text-sm">
                {tag ? "Be the first to post with this hashtag." : "Post the first update — a photo from the ramp, a trip status, or a hashtag."}
              </div>
            </div>
          )}

          {posts.map((p) => (
            <PostCard key={p.id} post={p} onChange={onChange} onDeleted={onDeleted} />
          ))}

          <div ref={sentinelRef} />
          {fetching && posts.length > 0 && (
            <div className="text-center py-4 text-white/40 text-sm">
              <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading more…
            </div>
          )}
          {!hasMore && posts.length > 0 && (
            <div className="text-center py-6 mono text-[11px] text-white/30 tracking-widest">// END OF FEED</div>
          )}
        </div>

        {/* Trending sidebar — sticky on desktop, hidden inline on mobile via lg: */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-4">
            <div className="card !p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-sky" />
                <div className="mono text-[10px] tracking-[0.24em] text-white/60 uppercase">// TRENDING · 30D</div>
              </div>
              {trending.length === 0 ? (
                <div className="text-white/40 text-sm">No hashtags yet.</div>
              ) : (
                <div className="space-y-1">
                  {trending.map((t) => (
                    <Link
                      key={t.tag}
                      to={`/feed?tag=${t.tag}`}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors ${tag === t.tag ? "bg-sky/10" : ""}`}
                      data-testid={`trending-${t.tag}`}
                    >
                      <span className="flex items-center gap-1.5 text-sm text-white">
                        <Hash className="h-3 w-3 text-sky" />
                        {t.tag}
                      </span>
                      <span className="mono text-[11px] text-white/40">{t.count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="card !p-4">
              <div className="mono text-[10px] tracking-[0.24em] text-white/60 uppercase mb-2">// QUICK POST</div>
              <div className="text-sm text-white/70 leading-relaxed">
                Tag airports with <span className="text-sky mono">#KTEB</span>, an OEM like <span className="text-sky mono">#Gulfstream</span>, or check in with a <span className="text-avail">verified tail</span>.
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
